import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface DicDetailRow {
  level_no: number;
  level_label: string;
  child_code: string;
  child_short_desc: string;
  dep_lessons: string;
  remarks: string;
  row_color: string;   // "red" or ""
}

interface ApiResponse {
  dic_file_name: string;
  country: string;
  title: string;
  rows: DicDetailRow[];
  error_mesg: string;
}

// interface Props {
//   dicFileName: string;
//   country:     string;
// }

const AlertModal: React.FC<{
  text: string;
  type: "E" | "A" | "I";
  onClose: () => void;
}> = ({ text, type, onClose }) => {
  const titleBg = type === "E" ? "#CC0000" : type === "I" ? "#119ECC" : "#2f6089";
  const titleLabel = type === "E" ? "✕ Error" : type === "I" ? "i Information" : "! Alert";
  return (
    <>
      <div style={overlayStyle} />
      <div style={alertBoxStyle}>
        <div style={{
          background: titleBg, color: "#fff", padding: "6px 12px",
          fontWeight: "bold", fontSize: 13
        }}>
          {titleLabel}
        </div>
        <div style={{
          padding: "18px 24px", textAlign: "center",
          fontFamily: "Tahoma,Arial", fontSize: 13, flex: 1
        }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
        <div style={{ textAlign: "center", paddingBottom: 14 }}>
          <button style={okBtnStyle} onClick={onClose}>OK</button>
        </div>
      </div>
    </>
  );
};

const EL_CheckDIC: React.FC = () => {
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [alertText, setAlertText] = useState("");
  const [alertType, setAlertType] = useState<"E" | "A" | "I">("E");
  const [alertOpen, setAlertOpen] = useState(false);

  const divScrollRef = useRef<HTMLDivElement>(null);
  const divContRef = useRef<HTMLDivElement>(null);
  const divHeadRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const dicFileName = queryParams.get("dicFileName") || "";
  const country = queryParams.get("country") || "";

  useEffect(() => {
    if (!dicFileName) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}api/check-dic/?DicFileName=${encodeURIComponent(dicFileName)}&Country=${encodeURIComponent(country)}`
        );
        const json = await res.json() as ApiResponse;
        setData(json);

        // mirrors: gfn_ShowMessage(ErrorMesg, "E")
        if (json.error_mesg) {
          setAlertText(json.error_mesg);
          setAlertType("E");
          setAlertOpen(true);
        }
      } catch (e) {
        setAlertText(String(e));
        setAlertType("E");
        setAlertOpen(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [dicFileName, country]);

  const setSize = useCallback(() => {
    const h = window.innerHeight - 50;
    const w = window.innerWidth - 30;
    if (divScrollRef.current) {
      divScrollRef.current.style.height = `${h}px`;
      divScrollRef.current.style.width = `${w}px`;
    }

    if (divScrollRef.current && divContRef.current && divHeadRef.current) {
      const titleEl = document.getElementById("lsn_divTitle");
      const titleH = titleEl ? titleEl.offsetHeight : 0;
      const headH = divHeadRef.current.offsetHeight;
      const outer = divScrollRef.current.offsetHeight;
      const contH = outer - titleH - headH;
      if (divContRef.current) {
        divContRef.current.style.height = `${Math.max(contH, 1)}px`;
      }
    }
  }, []);

  useEffect(() => {
    const t1 = setTimeout(setSize, 100);
    const t2 = setTimeout(setSize, 300);
    window.addEventListener("resize", setSize);
    return () => {
      clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener("resize", setSize);
    };
  }, [setSize, data]);

  const { totalRows, errorRows, missingLessons, missingTests, missingDeps } =
    useMemo(() => {
      if (!data) return { totalRows: 0, errorRows: 0, missingLessons: 0, missingTests: 0, missingDeps: 0 };
      let ml = 0, mt = 0, md = 0, err = 0;
      for (const r of data.rows) {
        if (r.row_color === "red") {
          err++;
          if (r.remarks.includes("Missing Lesson") && !r.remarks.includes("Dependent")) ml++;
          else if (r.remarks.includes("Missing Test")) mt++;
          else if (r.remarks.includes("Dependent lesson")) md++;
        }
      }
      return { totalRows: data.rows.length, errorRows: err, missingLessons: ml, missingTests: mt, missingDeps: md };
    }, [data]);

  return (
    <>
      {alertOpen && (
        <AlertModal
          text={alertText}
          type={alertType}
          onClose={() => setAlertOpen(false)}
        />
      )}

      <div style={{ margin: 10, fontFamily: "Arial, Tahoma, sans-serif", fontSize: "10pt" }}>

        {loading && (
          <div style={{ padding: 40, textAlign: "center", color: "#888" }}>
            Loading validation data...
          </div>
        )}

        {!loading && data && (
          <>
            <div
              id="lsn_divScrollableTable"
              ref={divScrollRef}
              style={{ position: "relative", overflow: "hidden", boxSizing: "border-box" }}
            >
              <div
                id="lsn_divTitle"
                style={{
                  width: "100%",
                  border: `solid 1px ${BORDER_COLOR}`,
                  borderBottom: "none",
                  color: TEXT_COLOR_HEADER,
                  backgroundColor: BACK_COLOR_HEADER,
                  fontFamily: FONT_FAMILY,
                  fontSize: FONT_SIZE,
                  fontWeight: "bold",
                  padding: "4px 8px",
                  boxSizing: "border-box",
                }}
              >
                Validation list for DicFile :{" "}
                <span style={{ fontSize: "14pt" }}>{dicFileName}</span>
                &nbsp;&nbsp;&nbsp;&nbsp;Country:{" "}
                <span style={{ fontSize: "14pt" }}>{country}</span>

                {errorRows > 0 && (
                  <span style={{
                    marginLeft: 24, fontSize: "9pt",
                    color: "red", fontWeight: "bold",
                  }}>
                    {errorRows} error(s): {missingLessons > 0 ? `${missingLessons} missing lesson(s) ` : ""}
                    {missingTests > 0 ? `${missingTests} missing test(s) ` : ""}
                    {missingDeps > 0 ? `${missingDeps} missing dep(s)` : ""}
                  </span>
                )}
                {errorRows === 0 && totalRows > 0 && (
                  <span style={{ marginLeft: 24, fontSize: "9pt", color: "green" }}>
                    ✓ All {totalRows} entries valid
                  </span>
                )}
              </div>
              <div
                id="lsn_divHead"
                ref={divHeadRef}
                style={{
                  width: "100%",
                  overflowX: "hidden",
                  border: `solid 1px ${BORDER_COLOR}`,
                  borderBottom: "none",
                  backgroundColor: BACK_COLOR_HEADER,
                  boxSizing: "border-box",
                }}
              >
                <table
                  id="lsn_tblHead"
                  style={tableStyle}
                  cellPadding={0} cellSpacing={0}
                >
                  <colgroup>
                    {COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
                  </colgroup>
                  <thead>
                    <tr id="lsn_trh">
                      {HEADERS.map((h, i) => (
                        <th
                          key={i}
                          id={`lsn_tdh${i}`}
                          style={{
                            ...thStyle,
                            textAlign: "left",
                          }}
                        >
                          <div id={`lsn_divh${i}`}>{h}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>

              <div
                id="lsn_divCont"
                ref={divContRef}
                style={{
                  width: "100%",
                  overflowY: "auto",
                  overflowX: "hidden",
                  border: `solid 1px ${BORDER_COLOR}`,
                  boxSizing: "border-box",
                }}
              >
                <table
                  id="lsn_tblCont"
                  style={tableStyle}
                  cellPadding={2} cellSpacing={0}
                >
                  <colgroup>
                    {COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
                  </colgroup>
                  <tbody>
                    {data.rows.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                          No data found for {dicFileName}.
                        </td>
                      </tr>
                    )}
                    {data.rows.map((row, idx) => {
                      const rowNo = idx + 1;         // 1-based (mirrors lsn_tr{n})
                      const isEven = idx % 2 === 0;
                      // mirrors: m_RowBackColor="#F4F4FF", m_RowAltBackColor="#FFFFFF"
                      const rowBg = isEven ? ROW_BACK : ROW_ALT_BACK;
                      // mirrors: RowTextColors: "red" for error rows
                      const textColor = row.row_color === "red" ? "red" : TEXT_COLOR;

                      return (
                        <tr
                          key={idx}
                          id={`lsn_tr${rowNo}`}
                          style={{
                            backgroundColor: rowBg,
                            color: textColor,

                            // borderBottom: `1px solid ${GRID_LINE_COLOR}`,
                          }}
                        >
                          {/* Col 0: L/T  (fn_DecodeData result) – Align=left */}
                          <td id={`lsn_td${rowNo}_0`} style={tdStyle}>
                            <div>{row.level_label}</div>
                          </td>

                          {/* Col 1: Lesson Code – Align=left */}
                          <td id={`lsn_td${rowNo}_1`} style={tdStyle}>
                            <div>{row.child_code}</div>
                          </td>

                          {/* Col 2: Description – Align=left */}
                          <td id={`lsn_td${rowNo}_2`} style={tdStyle}>
                            <div>{row.child_short_desc}</div>
                          </td>

                          {/* Col 3: Dependent Lessons – Align=left */}
                          <td id={`lsn_td${rowNo}_3`} style={tdStyle}>
                            <div>{row.dep_lessons}</div>
                          </td>

                          {/* Col 4: Remarks – Align=left, HTML content */}
                          <td id={`lsn_td${rowNo}_4`} style={tdStyle}>
                            <div
                              dangerouslySetInnerHTML={{ __html: row.remarks || "&nbsp;" }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const HEADERS = ["L/T", "Lesson Code", "Description", "Dependent Lessons", "Remarks"];
const COL_WIDTHS = ["8%", "12%", "25%", "25%", "30%"];

const FONT_FAMILY = "Arial";
const FONT_SIZE = "10pt";
const TEXT_COLOR_HEADER = "#2859ac";
const BACK_COLOR_HEADER = "#C7DDFD";
const BORDER_COLOR = "#80A0FF";
const GRID_LINE_COLOR = "#A3C0E8";
const TEXT_COLOR = "black";
const ROW_BACK = "#f4faff";
const ROW_ALT_BACK = "#FFFFFF";

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed",
  fontFamily: FONT_FAMILY,
  fontSize: FONT_SIZE,
};

const thStyle: React.CSSProperties = {
  fontFamily: FONT_FAMILY,
  fontSize: FONT_SIZE,
  color: TEXT_COLOR_HEADER,
  backgroundColor: BACK_COLOR_HEADER,
  fontWeight: "bold",
  padding: "4px 6px",
  border: `1px solid ${GRID_LINE_COLOR}`,
  textAlign: "left",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  fontFamily: FONT_FAMILY,
  fontSize: FONT_SIZE,
  padding: "6px 6px",
  borderRight: `1px solid ${GRID_LINE_COLOR}`,
  verticalAlign: "top",
  wordBreak: "break-word",
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  backgroundColor: "#cccccc",
  opacity: 0.6,
  zIndex: 99999,
};

const alertBoxStyle: React.CSSProperties = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  width: 460,
  minHeight: 170,
  backgroundColor: "#fff",
  zIndex: 100000,
  border: "2px solid #CC0000",
  fontFamily: "Tahoma, Arial",
  display: "flex",
  flexDirection: "column",
};

const okBtnStyle: React.CSSProperties = {
  backgroundColor: "#2f6089",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "6px 28px",
  fontSize: 12,
  fontFamily: "Tahoma, Arial",
  cursor: "pointer",
  fontWeight: "bold",
};

export default EL_CheckDIC;